/**
 * 1.6.0 AI content description: the Log tab's suggestions (plan C1). Stream A's file (LogTab.tsx only mounts them;
 * docs/dev/mcp-1.6.0-build.md):
 *
 *  - SuggestionsBar (above the list): "N AI suggestions on <layer>" with Accept all on this layer
 *    (useAcceptAllSegments), one line per layer that has any;
 *  - SuggestionMark (in each segment row): for a pending suggestion (isSuggestion), "AI suggestion" with who
 *    (suggestedBy: mcp:<token> or flow:<run>) and Accept / Edit / Reject (useAcceptSegment, useRejectSegment); the row
 *    is shown in grey (class `segment-row suggestion`, styles block A). Edit opens the row's own editor (onOpen, as a
 *    double-click does); saving it accepts the suggestion (the segment PATCH, catalog/strata.ts);
 *  - the bar's "Suggestions only" lists only the pending suggestions (LogTab filters its list).
 */
import { Check, Pencil, Sparkles, X } from 'lucide-react';
import { isSuggestion, type AssetDetail, type SegmentDTO, type StratumDTO } from '@prism/contracts';
import { errorMessage } from '../../api/client';
import { useAcceptAllSegments, useAcceptSegment, useRejectSegment } from '../../api/hooks/suggestions';
import { toast } from '../../components/Toasts';

const pending = (s: SegmentDTO) => isSuggestion({ text: s.text, values: s.values, source: s.source ?? null, suggestedBy: s.suggestedBy ?? null, acceptedAt: s.acceptedAt ?? null });

/** Who suggested it, for people: "an AI assistant (Claude Desktop)" or "a workflow run". */
export function suggesterLabel(by: string | null | undefined): string {
  if (!by) return 'AI';
  if (by.startsWith('mcp:')) return `AI assistant (${by.slice(4)})`;
  if (by.startsWith('flow:')) return 'a workflow';
  return by;
}

/** A pending suggestion (accepted ones and a person's segments are not). */
export const isPendingSuggestion = pending;

export function SuggestionsBar({ asset, strata, segments, canEdit, onChanged, only = false, onOnly }: {
  asset: AssetDetail; strata: StratumDTO[]; segments: SegmentDTO[]; canEdit: boolean; onChanged: () => void;
  /** "Suggestions only": the list shows only the pending suggestions. */
  only?: boolean; onOnly?: (only: boolean) => void;
}) {
  const acceptAll = useAcceptAllSegments();
  const counts = strata.map((s) => ({ layer: s, n: segments.filter((x) => x.stratumId === s.id && pending(x)).length })).filter((c) => c.n > 0);
  if (!counts.length) return null;
  async function accept(stratumId: string, label: string) {
    try {
      const r = await acceptAll.mutateAsync({ assetId: asset.id, stratumId });
      toast(`Accepted ${r.accepted} suggestion${r.accepted === 1 ? '' : 's'} on ${label}`, 'info');
      onChanged();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <div className="ai-suggestions-bar" data-testid="suggestions-bar">
      {counts.map(({ layer, n }) => (
        <div key={layer.id} className="ai-suggestions-line" data-testid="suggestions-line" data-layer={layer.id}>
          <Sparkles size={14} aria-hidden />
          <span><strong>{n}</strong> AI suggestion{n === 1 ? '' : 's'} on <span style={{ color: layer.color }}>{layer.label}</span></span>
          {canEdit && (
            <button className="btn sm" disabled={acceptAll.isPending} onClick={() => void accept(layer.id, layer.label)} data-testid="accept-all">
              <Check size={12} /> Accept all on this layer
            </button>
          )}
        </div>
      ))}
      {onOnly && (
        <label className="ai-suggestions-only" data-testid="suggestions-only">
          <input type="checkbox" checked={only} onChange={(e) => onOnly(e.target.checked)} /> Suggestions only
        </label>
      )}
    </div>
  );
}

export function SuggestionMark({ asset, seg, canEdit, onChanged, onOpen }: {
  asset: AssetDetail; seg: SegmentDTO; canEdit: boolean; onChanged: () => void;
  /** Opens the row's editor (LogTab's one-at-a-time editing); without it, Edit double-clicks the text. */
  onOpen?: () => void;
}) {
  const accept = useAcceptSegment();
  const reject = useRejectSegment();
  if (!pending(seg)) return null;
  const busy = accept.isPending || reject.isPending;
  async function run(fn: () => Promise<unknown>, done: string) {
    try {
      await fn();
      toast(done, 'info');
      onChanged();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  /** Opens the row's own editor (the text's double-click); saving there accepts the suggestion. */
  function edit(e: React.MouseEvent<HTMLButtonElement>) {
    if (onOpen) { onOpen(); return; }
    const text = e.currentTarget.closest('.segment-body')?.lastElementChild;
    text?.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
  }
  return (
    <span className="ai-suggestion-mark" data-testid="suggestion-mark" title={`Suggested by ${suggesterLabel(seg.suggestedBy)}${seg.suggestedAt ? ` on ${new Date(seg.suggestedAt).toLocaleString()}` : ''}`}>
      <span className="ai-suggestion-pill"><Sparkles size={11} aria-hidden /> AI suggestion</span>
      {canEdit && (
        <>
          <button className="btn ghost sm" disabled={busy} onClick={() => void run(() => accept.mutateAsync({ assetId: asset.id, segmentId: seg.id }), 'Suggestion accepted')} data-testid="suggestion-accept">
            <Check size={12} /> Accept
          </button>
          <button className="btn ghost sm" disabled={busy} onClick={edit} data-testid="suggestion-edit" title="Edit it (saving accepts it)"><Pencil size={12} /> Edit</button>
          <button className="btn ghost sm danger" disabled={busy} onClick={() => void run(() => reject.mutateAsync({ assetId: asset.id, segmentId: seg.id }), 'Suggestion rejected')} data-testid="suggestion-reject">
            <X size={12} /> Reject
          </button>
        </>
      )}
    </span>
  );
}
